import i18n from '../i18n/i18n';

/**
 * Translation utility for converting job fields to selected language using react-i18next
 */
class TranslationUtils {
  /**
   * Translate specific fields in job objects
   * @param {Array} jobs - Array of job objects
   * @param {string} targetLanguage - Target language code (e.g., 'hi', 'gu', 'ta')
   * @param {Array} fieldsToTranslate - Fields to translate (default: ['jobName', 'jobDetails', 'cityName'])
   * @returns {Promise<Array>} Array of jobs with translated fields
   */
  static async translateJobFields(jobs, targetLanguage = 'en', fieldsToTranslate = ['jobName', 'jobDetails', 'cityName']) {
    // Skip translation if target language is English or no jobs
    if (!jobs || jobs.length === 0 || targetLanguage === 'en') {
      return jobs;
    }

    console.log(`[TranslationUtils] Translating ${jobs.length} jobs to language: ${targetLanguage}`);
    console.log(`[TranslationUtils] Fields to translate: ${fieldsToTranslate.join(', ')}`);

    try {
      // Change i18next language
      await i18n.changeLanguage(targetLanguage);

      // Process jobs synchronously (no API calls needed)
      const translatedJobs = jobs.map((job) => {
        const translatedJob = { ...job };

        // Translate each specified field
        for (const field of fieldsToTranslate) {
          if (job[field] && typeof job[field] === 'string') {
            const originalText = job[field];
            const translatedText = this.translateText(originalText, targetLanguage);
            translatedJob[field] = translatedText;
            
            console.log(`[TranslationUtils] Translated ${field}: "${originalText}" -> "${translatedText}"`);
          }
        }

        return translatedJob;
      });

      console.log(`[TranslationUtils] Successfully translated ${translatedJobs.length} jobs`);
      return translatedJobs;

    } catch (error) {
      console.error('[TranslationUtils] Error during job translation:', error);
      return jobs; // Return original jobs if translation fails
    }
  }

  /**
   * Translate a single text field using i18next
   * @param {string} text - Text to translate
   * @param {string} targetLanguage - Target language code
   * @returns {string} Translated text
   */
  static translateText(text, targetLanguage = 'en') {
    if (!text || targetLanguage === 'en') {
      return text;
    }

    try {
      // Use i18next to get translation
      const translatedText = i18n.t(text, { lng: targetLanguage });
      
      // If translation not found, return original text
      return translatedText !== text ? translatedText : text;
    } catch (error) {
      console.warn('[TranslationUtils] Failed to translate text:', error);
      return text;
    }
  }

  /**
   * Batch translate multiple texts
   * @param {Array<string>} texts - Array of texts to translate
   * @param {string} targetLanguage - Target language code
   * @returns {Promise<Array<string>>} Array of translated texts
   */
  static async translateTexts(texts, targetLanguage = 'en') {
    if (!texts || texts.length === 0 || targetLanguage === 'en') {
      return texts;
    }

    try {
      const translatedTexts = await Promise.all(
        texts.map(text => this.translateText(text, targetLanguage))
      );
      return translatedTexts;
    } catch (error) {
      console.error('[TranslationUtils] Error during batch translation:', error);
      return texts;
    }
  }

  /**
   * Check if translation is needed
   * @param {string} targetLanguage - Target language code
   * @returns {boolean} True if translation is needed
   */
  static isTranslationNeeded(targetLanguage) {
    return targetLanguage && targetLanguage !== 'en';
  }
}

export default TranslationUtils;
